import React, { useEffect, useMemo, useRef, useState } from 'react';
import { LuDownload, LuInfo, LuRefreshCw } from 'react-icons/lu';

import usePermissions from '../../../components/Menu/MenuItems/hooks/usePermissions';
import useDashboardFilters from './hooks/useDashboardFilters';
import useDashboardCatalogs from './hooks/useDashboardCatalogs';
import useCreditSalesData from './hooks/useCreditSalesData';

import AgingBar from './components/AgingBar';
import BreakdownPanel from './components/BreakdownPanel';
import CoverageBanner from './components/CoverageBanner';
import CreditQuality from './components/CreditQuality';
import DataQualityPanel from './components/DataQualityPanel';
import FiltersBar from './components/FiltersBar';
import ForecastPanel from './components/ForecastPanel';
import PeriodChips from './components/PeriodChips';
import PortfolioTiles from './components/PortfolioTiles';
import VintagePanel from './components/VintagePanel';
import { BlockErrorState, BlockSkeleton } from './components/BlockState';

import { ORIGIN_COVERAGE_START, SECTION_TABS } from './constants';
import { formatIsoDate, pluralTitulos } from './utils/format';
import {
   Card,
   EmptyCard,
   EmptyText,
   EmptyTitle,
   SectionTab,
   SectionTabs,
   ShellButton,
   ShellPrimaryButton,
   Stack,
   StickyLabel,
   StickyPeriodBar,
   StickyRange,
} from './styles';

import { Container } from '../../../styles/layout/Container';
import {
   PageHeader,
   PageHeaderActions,
   PageHeaderText,
   PageSubtitle,
   PageTitle,
} from '../../../styles/layout/PageHeader';
import { GhostButton } from '../../../styles/ui/Button';

// ---------------------------------------------------------------------------
// Financeiro › Vendas a prazo e adimplência (handoff 0084).
//
// Painel de crediário: responde "como estão minhas vendas a prazo?" e "os
// clientes estão pagando em dia?" sobre os títulos emitidos via boleto/PIX.
// Consome exclusivamente /billing/analytics/* — nada é calculado a partir de
// dado que o backend não expõe.
//
// Substitui o antigo BillingDashboard na mesma rota (/billing/dashboard). O
// gate de permissão (`finance:dashboard:read`) é da ROTA, não daqui.
//
// Nota de escopo: o design previa uma topbar de breadcrumb "Financeiro › Vendas
// a prazo". O AppShell tem título fixo por app, sem mecanismo de breadcrumb por
// rota — introduzir um mexeria em todas as telas, fora do escopo desta tarefa.
// O PageHeader carrega a identificação da tela.
// ---------------------------------------------------------------------------

export default function CreditSalesDashboard() {
   const { hasPermission } = usePermissions();

   const {
      globalFilters,
      tab,
      by,
      thresholdDias,
      setTab,
      setDateRange,
      setDateBasis,
      setOrigins,
      setBy,
      setThresholdDias,
      clearAll,
   } = useDashboardFilters();

   // Seleção de safras do gráfico. Mora aqui, e não no VintagePanel, porque o
   // painel é desmontado ao trocar de aba — perder a comparação que a pessoa
   // acabou de montar seria o pior efeito colateral da divisão em abas.
   const [vintageView, setVintageView] = useState({
      selectedKeys: null,
      isolated: null,
   });

   const { origins, bankAccounts } = useDashboardCatalogs({
      // Permissão DIFERENTE da que abre o dashboard — sem ela, o grupo de CNPJ
      // simplesmente não existe (e com uma conta só ele já ficaria oculto).
      canReadBankAccounts: hasPermission('finance:bank_account:read'),
   });

   const {
      summary,
      aging,
      breakdown,
      forecast,
      vintage,
      referenceDate,
      dataQuality,
      fieldErrors,
      refresh,
   } = useCreditSalesData({ globalFilters, by, thresholdDias });

   const [showQuality, setShowQuality] = useState(false);

   // Barra de período fixa: desce quando a barra de filtros sai da tela e
   // recolhe quando ela reaparece. O gatilho é a VISIBILIDADE do elemento real,
   // não uma altura de scroll em pixel — que quebraria a cada mudança de
   // layout (painel de qualidade aberto, banner de cobertura presente).
   const filtersRef = useRef(null);
   const [periodBarVisible, setPeriodBarVisible] = useState(false);
   // Geometria da barra fixa, copiada da barra de filtros real: ela é `fixed`
   // (para descer sobre o conteúdo), mas não pode atravessar o sidebar. Medir o
   // elemento em vez de chutar um offset faz a barra acompanhar o colapso do
   // menu e o max-width do container sem a página saber que eles existem.
   const [periodBarBox, setPeriodBarBox] = useState(null);

   useEffect(() => {
      const node = filtersRef.current;
      if (!node || typeof IntersectionObserver === 'undefined') return undefined;

      const observer = new IntersectionObserver(
         ([entry]) => setPeriodBarVisible(!entry.isIntersecting),
         { threshold: 0 },
      );
      observer.observe(node);
      return () => observer.disconnect();
   }, []);

   useEffect(() => {
      const node = filtersRef.current;
      if (!node) return undefined;

      const measure = () => {
         const rect = node.getBoundingClientRect();
         setPeriodBarBox({ left: rect.left, width: rect.width });
      };
      measure();

      // O ResizeObserver cobre o colapso do menu (que muda a largura da área
      // principal); o resize da janela cobre o resto.
      const resizeObserver =
         typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
      resizeObserver?.observe(node);
      window.addEventListener('resize', measure);

      return () => {
         resizeObserver?.disconnect();
         window.removeEventListener('resize', measure);
      };
   }, []);

   // Contagem é a fonte da verdade (0084 §5.8): a tela só se declara vazia
   // depois que o summary chegou de fato — enquanto carrega, é skeleton.
   const isEmpty =
      !summary.loading &&
      !summary.error &&
      summary.data?.emitido?.titulos === 0;

   // Banner de cobertura: aparece quando o operador está LENDO POR ORIGEM — seja
   // porque filtrou por origem, seja porque está na aba Departamento — e o
   // período alcança a carga histórica, que entrou sem departamento gerador.
   //
   // O gatilho inclui a aba (e não só o filtro) porque o problema é da LEITURA,
   // não do recorte: com o período inteiro, 71,1% do valor cai em "sem
   // classificação" e o ranking de departamentos descreve menos de um terço da
   // operação. Recortando em mar/2026 esse buraco cai para 22,9% (0084 §7.5).
   const periodReachesHistoricalLoad =
      !globalFilters.from || globalFilters.from < ORIGIN_COVERAGE_START;
   //
   // Com a tela em abas, o aviso só faz sentido onde a leitura por origem
   // acontece: na aba de quebra (com `by=origin`) ou quando o recorte global
   // filtra origem — que afeta todas as abas.
   const readingByOrigin =
      globalFilters.originIds.length > 0 ||
      (tab === 'breakdown' && by === 'origin');
   const showCoverageBanner = readingByOrigin && periodReachesHistoricalLoad;

   const subtitle = useMemo(() => {
      const parts = ['Crediário via boleto/PIX'];
      if (referenceDate) parts.push(`referência ${formatIsoDate(referenceDate)}`);
      if (summary.data?.emitido?.titulos !== undefined) {
         parts.push(`${pluralTitulos(summary.data.emitido.titulos)} no recorte`);
      }
      parts.push('histórico desde jan/2022');
      return parts.join(' · ');
   }, [referenceDate, summary.data]);

   // Cada dobra decide sozinha entre skeleton, erro e conteúdo. Nenhuma espera
   // pela outra — é a razão de as 5 chamadas serem paralelas.
   const renderBlock = (block, content, skeleton) => {
      if (block.loading && !block.data) {
         return <BlockSkeleton rows={skeleton.rows} height={skeleton.height} />;
      }
      if (block.error) {
         return <BlockErrorState error={block.error} onRetry={refresh} />;
      }
      return content;
   };

   const periodRangeLabel =
      globalFilters.from || globalFilters.to
         ? `${globalFilters.from ? formatIsoDate(globalFilters.from) : 'início'} — ${globalFilters.to ? formatIsoDate(globalFilters.to) : 'hoje'}`
         : 'Todo o histórico';

   return (
      <Container $size="xl">
         {periodBarBox ? (
            <StickyPeriodBar
               $visible={periodBarVisible}
               aria-hidden={!periodBarVisible}
               style={{ left: periodBarBox.left, width: periodBarBox.width }}
            >
               <StickyLabel>Período de emissão</StickyLabel>
               <PeriodChips
                  tone="dark"
                  from={globalFilters.from}
                  to={globalFilters.to}
                  referenceDate={referenceDate}
                  onSelect={setDateRange}
               />
               <StickyRange>{periodRangeLabel}</StickyRange>
            </StickyPeriodBar>
         ) : null}

         <PageHeader>
            <PageHeaderText>
               <PageTitle>Vendas a prazo e adimplência</PageTitle>
               <PageSubtitle>{subtitle}</PageSubtitle>
            </PageHeaderText>

            <PageHeaderActions>
               <ShellButton
                  type="button"
                  aria-expanded={showQuality}
                  onClick={() => setShowQuality((open) => !open)}
               >
                  <LuInfo size={16} strokeWidth={1.8} aria-hidden="true" />
                  Qualidade dos dados
               </ShellButton>

               <ShellButton type="button" onClick={refresh}>
                  <LuRefreshCw size={17} strokeWidth={1.8} aria-hidden="true" />
                  Atualizar
               </ShellButton>

               {/* Desabilitado de propósito: não existe endpoint de exportação
                   em /billing/analytics/*. Botão que não faz nada é pior que
                   botão ausente — este ao menos diz por que não faz. */}
               <ShellPrimaryButton
                  type="button"
                  disabled
                  title="Exportação ainda não disponível para este painel."
               >
                  <LuDownload size={16} strokeWidth={1.8} aria-hidden="true" />
                  Exportar
               </ShellPrimaryButton>
            </PageHeaderActions>
         </PageHeader>

         <Stack>
            {showQuality ? (
               <DataQualityPanel
                  dataQuality={dataQuality}
                  onClose={() => setShowQuality(false)}
               />
            ) : null}

            <div ref={filtersRef}>
               <FiltersBar
                  filters={globalFilters}
                  referenceDate={referenceDate}
                  origins={origins}
                  fieldErrors={fieldErrors}
                  onDateRangeChange={setDateRange}
                  onDateBasisChange={setDateBasis}
                  onOriginsChange={setOrigins}
               />
            </div>

            {showCoverageBanner ? (
               <CoverageBanner
                  unclassified={breakdown.data?.sem_classificacao ?? null}
                  coverageStart={formatIsoDate(ORIGIN_COVERAGE_START)}
                  onApplyCoverageStart={() =>
                     setDateRange({ from: ORIGIN_COVERAGE_START })
                  }
               />
            ) : null}

            {isEmpty ? (
               <EmptyCard>
                  <EmptyTitle>Nenhum título emitido no recorte</EmptyTitle>
                  <EmptyText>
                     A carga histórica do ASAAS se encerra em fev/2026 — não há
                     títulos sem classificação no período selecionado. Meses sem
                     títulos são zero real, não falha de coleta.
                  </EmptyText>
                  <GhostButton type="button" onClick={clearAll}>
                     Limpar filtros
                  </GhostButton>
               </EmptyCard>
            ) : (
               <>
                  {/* As abas MONTAM E DESMONTAM o conteúdo — não é `display:
                      none`, que manteria o custo de layout e paint da tela
                      inteira (o heatmap de safra sozinho passa de 250 células).
                      O recorte segue global e as 5 chamadas seguem paralelas:
                      a aba muda o que está na tela, não o que foi perguntado. */}
                  <SectionTabs aria-label="Seções do painel">
                     {SECTION_TABS.map((section) => (
                        <SectionTab
                           key={section.key}
                           type="button"
                           $active={tab === section.key}
                           aria-pressed={tab === section.key}
                           onClick={() => setTab(section.key)}
                        >
                           {section.label}
                        </SectionTab>
                     ))}
                  </SectionTabs>

                  {tab === 'portfolio' ? (
                     <>
                        {/* Os tiles tratam o próprio carregamento por tile (vêm
                            de dois endpoints distintos); só a barra de aging
                            passa pelo renderBlock. */}
                        <PortfolioTiles summary={summary} aging={aging} />

                        <Card>
                           {renderBlock(
                              aging,
                              aging.data ? <AgingBar aging={aging.data} /> : null,
                              { rows: 3, height: 44 },
                           )}
                        </Card>

                        <Card>
                           {renderBlock(
                              summary,
                              summary.data ? (
                                 <CreditQuality
                                    summary={summary.data}
                                    dateBasis={globalFilters.dateBasis}
                                 />
                              ) : null,
                              { rows: 2, height: 60 },
                           )}
                        </Card>

                        <Card>
                           {renderBlock(
                              forecast,
                              forecast.data ? (
                                 <ForecastPanel forecast={forecast.data} />
                              ) : null,
                              { rows: 3, height: 30 },
                           )}
                        </Card>
                     </>
                  ) : null}

                  {tab === 'breakdown' ? (
                     <Card>
                        <BreakdownPanel
                           block={breakdown}
                           by={by}
                           onChangeBy={setBy}
                           bankAccountsCount={bankAccounts.length}
                           onRetry={refresh}
                        />
                     </Card>
                  ) : null}

                  {tab === 'vintage' ? (
                     <Card>
                        <VintagePanel
                           block={vintage}
                           thresholdDias={thresholdDias}
                           onChangeThreshold={setThresholdDias}
                           onRetry={refresh}
                           view={vintageView}
                           onViewChange={setVintageView}
                        />
                     </Card>
                  ) : null}
               </>
            )}
         </Stack>
      </Container>
   );
}
